import type { RoomMember } from "@platform/features/video/types/video.types";

/**
 * Utility functions for room member data operations to eliminate code duplication.
 */

/**
 * Extract room member data from externalUserId and return comprehensive information
 * @param attendee - The attendee object with externalUserId
 * @param roomMembers - Map of user_id to RoomMember
 * @returns Object with displayName and roomMemberData
 */
export const extractRoomMemberData = (attendee: any, roomMembers?: Record<number, RoomMember>): { displayName: string; roomMemberData: RoomMember | null } => {
  let displayName = "";
  let roomMemberData: RoomMember | null = null;
  
  if (attendee?.externalUserId) {
    // Format: firstName-lastName-TS-{timestamp}-UID-{userId}
    // Extract name part (before -TS-) and user ID (after -UID-)
    const parts = attendee.externalUserId.split('-TS-');
    if (parts.length >= 2) {
      const namePart = parts[0];
      const afterTimestamp = parts[1];
      
      // Extract user ID from the end
      const uidParts = afterTimestamp.split('-UID-');
      if (uidParts.length >= 2) {
        const userId = parseInt(uidParts[1], 10);
        
        // Get display name
        displayName = namePart.replace('-', ' ');
        
        // Look up user data by ID if roomMembers is provided
        if (roomMembers && !isNaN(userId)) {
          roomMemberData = roomMembers[userId] || null;
        }
      } else {
        // Fallback for old format without user ID
        displayName = namePart.replace('-', ' ');
      }
    } else {
      // Fallback for old format
      displayName = attendee.externalUserId.replace('-', ' ');
    }
  } else {
    // Fallback to name field if externalUserId is not available
    displayName = attendee?.name || "";
  }

  return { displayName, roomMemberData };
};
